// routeAnimation.js
import {
  smoothRoute,
  removeDuplicatePoints,
  removeClosePoints,
  getStrokeWeightForZoom,
  sleep,
  fitBoundsWithOffset,
} from './mapHelpers.js'

// Tableau de couleurs TRÈS contrastées pour une visibilité maximale
const SEGMENT_COLORS = [
  '#FF7200', // Rouge vif - très contrastant
  '#0000FF', // Bleu pur - contraste maximal avec le rouge
  '#00FF00', // Vert vif - contraste avec bleu
  '#FF00FF', // Magenta - contraste avec vert
  '#00FFFF', // Cyan - contraste avec magenta
  '#FF0000', // Orange - contrasté avec cyan
  '#800080', // Violet profond - contrasté avec orange
  '#FFFF00', // Jaune vif - contrasté avec violet
  '#FF1493', // Rose profond - contrasté avec jaune
  '#008080', // Turquoise - contrasté avec rose
  '#FF4500', // Orange rougeâtre - contrasté avec turquoise
  '#4B0082', // Indigo - contrasté avec orange
]

/**
 * Anime un tracé sur la carte avec des segments colorés (basé sur les legs réels)
 * @param {Object} params
 * @param {Object} params.map - instance de Google Map
 * @param {Object} params.routesObj - objet contenant les routes par id
 * @param {string} params.routeId - identifiant de la route à animer
 * @param {Array} params.routeLegs - tableau de legs (chaque leg = tableau de points {lat,lng})
 * @param {Array} params.routePoints - ancien paramètre pour compatibilité (non utilisé)
 * @param {Object} params.options - options { color, totalTime, interval }
 * @param {AbortSignal} params.abortSignal - signal pour stopper l'animation
 */
export async function animateRoute({
  map,
  routesObj,
  routeId,
  routeLegs,
  routePoints, // Gardé pour compatibilité
  options = {},
  abortSignal,
}) {
  if (!map) {
    return
  }

  const {
    color = '#FF7200',
    totalTime = 3000,
    interval = 30,
    offsetY,
  } = options

  // Utiliser routeLegs si disponible, sinon routePoints pour compatibilité
  const legs = routeLegs || (routePoints ? [routePoints] : [])

  if (!legs || legs.length === 0) {
    return
  }

  // Nettoyer les anciennes routes pour ce routeId
  Object.keys(routesObj).forEach(key => {
    if (key.startsWith(routeId)) {
      delete routesObj[key]
    }
  })

  // Cas spécial : si un seul leg avec peu de points, créer une route simple
  if (legs.length === 1 && legs[0].length <= 2) {
    const poly = {
      path: legs[0],
      strokeColor: SEGMENT_COLORS[0], // Utiliser la première couleur
      strokeOpacity: 1,
      strokeWeight: getStrokeWeightForZoom(map.getZoom()),
      geodesic: true,
    }
    routesObj[routeId] = poly

    // Ajuster la vue
    const bounds = new google.maps.LatLngBounds()
    legs[0].forEach(pt => bounds.extend(pt))
    fitBoundsWithOffset(map, bounds, offsetY)
    return
  }

  // Créer les segments colorés à partir des legs réels
  const segments = {}
  const baseStrokeWeight = getStrokeWeightForZoom(map.getZoom())

  legs.forEach((leg, legIndex) => {
    if (leg && leg.length >= 2) {
      const segmentId = `${routeId}_segment_${legIndex}`
      const colorIndex = legIndex % SEGMENT_COLORS.length

      // Épaisseur simple et remarquable
      let strokeWeight
      if (legIndex === 0) {
        strokeWeight = baseStrokeWeight + 4 // Premier segment : +4px
      } else if (legIndex === 1) {
        strokeWeight = baseStrokeWeight + 2 // Deuxième segment : +2px
      } else {
        strokeWeight = baseStrokeWeight // Autres segments : normal
      }

      segments[segmentId] = {
        path: smoothRoute(
          removeClosePoints(removeDuplicatePoints(leg), 0.0001),
          5,
        ),
        strokeColor: SEGMENT_COLORS[colorIndex],
        strokeOpacity: 1,
        strokeWeight: strokeWeight,
        geodesic: true,
        legIndex: legIndex,
        // z-index direct : premier segment en dessous, derniers au-dessus
        // On s'assure que le premier segment a le z-index le plus bas
        zIndex: legIndex + 1,
      }
    }
  })

  // Ajuster la vue avec tous les points de tous les legs
  const bounds = new google.maps.LatLngBounds()
  legs.forEach(leg => {
    leg.forEach(pt => bounds.extend(pt))
  })
  fitBoundsWithOffset(map, bounds, offsetY)

  // Créer les segments dans l'ordre inverse pour garantir l'ordre d'affichage
  // (créer d'abord les segments finis, puis les plus épais au-dessus)
  const segmentIds = Object.keys(segments).sort((a, b) => {
    const indexA = parseInt(a.split('_segment_')[1])
    const indexB = parseInt(b.split('_segment_')[1])
    return indexB - indexA // Ordre inverse : dernier d'abord, premier en dernier
  })

  const totalSegments = segmentIds.length
  const timePerSegment = totalTime / totalSegments

  // Créer d'abord tous les segments vides dans le bon ordre d'affichage
  segmentIds.forEach(segmentId => {
    routesObj[segmentId] = {
      ...segments[segmentId],
      path: [], // Commencer avec un chemin vide
    }
  })

  // Puis animer dans le bon ordre séquentiel (du premier au dernier)
  const animationOrder = Object.keys(segments).sort((a, b) => {
    const indexA = parseInt(a.split('_segment_')[1])
    const indexB = parseInt(b.split('_segment_')[1])
    return indexA - indexB
  })

  // Animer les segments séquentiellement dans le bon ordre
  for (let segIndex = 0; segIndex < animationOrder.length; segIndex++) {
    if (abortSignal?.aborted) break

    const segmentId = animationOrder[segIndex]
    const segment = segments[segmentId]
    const segmentStartTime = performance.now()

    // Animation de ce segment spécifique
    while (true) {
      if (abortSignal?.aborted) break

      const segmentElapsed = performance.now() - segmentStartTime
      const segmentProgress = Math.min(segmentElapsed / timePerSegment, 1)

      const totalSegmentPoints = segment.path.length
      const pointsToShow = Math.floor(totalSegmentPoints * segmentProgress)

      if (pointsToShow > 0) {
        routesObj[segmentId] = {
          ...segment,
          path: segment.path.slice(0, pointsToShow),
        }
      }

      if (segmentProgress >= 1) break
      await sleep(interval)
    }

    // Petite pause entre les segments pour mieux visualiser la séquentialité
    if (segIndex < animationOrder.length - 1) {
      await sleep(100)
    }
  }
}
